import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { PublicCourseCard } from "@/components/PublicCourseCard";
import { Input } from "@/components/ui/input";
import { Loader2, GraduationCap, Sparkles, Package, Laptop, Video, Guitar, Music, BookOpen, Calendar, Search } from "lucide-react";
import SEO from "@/components/SEO";
import { FACHKRAEFTE_KURZ, KURSE_TEXT, WEBSEITE_FAKTEN, gross } from "@/config/webseiteFakten";

// Category-specific hero configurations
const categoryHeroConfig: Record<string, {
  badge: string;
  badgeIcon: React.ReactNode;
  title: string;
  description: string;
  gradient: string;
  blob1: string;
  blob2: string;
  accentColor: string;
}> = {
  default: {
    badge: "Online-Akademie",
    badgeIcon: <Sparkles className="w-4 h-4" />,
    title: "Musik lebendig vermitteln",
    description: "Entdecke praxiserprobte Kurse für musikalische Früherziehung. Fertige Stundenbilder, inspirierende Fortbildungen und wertvolle Webinar-Aufzeichnungen.",
    gradient: "from-primary/5 via-background to-background",
    blob1: "bg-primary/10",
    blob2: "bg-accent/10",
    accentColor: "text-primary",
  },
  kreativpakete: {
    badge: "Kreativpakete",
    badgeIcon: <Package className="w-4 h-4" />,
    title: "Fertige Stundenbilder für deinen Kita-Alltag",
    description: "Spare Zeit und begeistere Kinder! Unsere Kreativpakete enthalten alles, was du brauchst: Lieder, Bewegungsspiele, Materialien und detaillierte Anleitungen.",
    gradient: "from-amber-500/10 via-orange-500/5 to-background",
    blob1: "bg-amber-500/15",
    blob2: "bg-orange-500/10",
    accentColor: "text-amber-600",
  },
  "online-fortbildungen": {
    badge: "Online-Fortbildungen",
    badgeIcon: <Laptop className="w-4 h-4" />,
    title: "Flexibel weiterbilden – von überall",
    description: "Vertiefe dein Wissen in deinem eigenen Tempo. Unsere strukturierten Online-Fortbildungen mit Videos, Arbeitsmaterialien und Zertifikat.",
    gradient: "from-blue-500/10 via-cyan-500/5 to-background",
    blob1: "bg-blue-500/15",
    blob2: "bg-cyan-500/10",
    accentColor: "text-blue-600",
  },
  "webinar-aufzeichnungen": {
    badge: "Webinar-Aufzeichnungen",
    badgeIcon: <Video className="w-4 h-4" />,
    title: "Live-Wissen – jetzt als Aufzeichnung",
    description: "Verpasst? Kein Problem! Schau dir unsere beliebten Live-Webinare in Ruhe an und lerne von Expert:innen der musikalischen Früherziehung.",
    gradient: "from-purple-500/10 via-pink-500/5 to-background",
    blob1: "bg-purple-500/15",
    blob2: "bg-pink-500/10",
    accentColor: "text-purple-600",
  },
  "gitarre-ukulele": {
    badge: "Gitarre & Ukulele",
    badgeIcon: <Guitar className="w-4 h-4" />,
    title: "Saiteninstrumente für die Kita meistern",
    description: "Lerne Gitarre oder Ukulele speziell für den pädagogischen Einsatz. Von Grundlagen bis zur Liedbegleitung – praxisnah und kindgerecht.",
    gradient: "from-emerald-500/10 via-teal-500/5 to-background",
    blob1: "bg-emerald-500/15",
    blob2: "bg-teal-500/10",
    accentColor: "text-emerald-600",
  },
  liederwerkstatt: {
    badge: "Liederwerkstatt",
    badgeIcon: <Music className="w-4 h-4" />,
    title: "Gemeinsam singen & musizieren – live!",
    description: "Alle 14 Tage treffen wir uns online zum gemeinsamen Singen und Musizieren. Gitarre, Ukulele, Gesang – werde Teil unserer musikalischen Community.",
    gradient: "from-rose-500/10 via-red-500/5 to-background",
    blob1: "bg-rose-500/15",
    blob2: "bg-red-500/10",
    accentColor: "text-rose-600",
  },
  journalartikel: {
    badge: "Journalartikel",
    badgeIcon: <BookOpen className="w-4 h-4" />,
    title: "Wissen kompakt – zum Lesen & Vertiefen",
    description: "Fachartikel, Tipps und Hintergrundwissen rund um musikalische Früherziehung. Ideal zum Nachschlagen und Inspirieren lassen.",
    gradient: "from-slate-500/10 via-gray-500/5 to-background",
    blob1: "bg-slate-500/15",
    blob2: "bg-gray-500/10",
    accentColor: "text-slate-600",
  },
  musikonzeptag: {
    badge: "MusiKonzeptag",
    badgeIcon: <Calendar className="w-4 h-4" />,
    title: "Unser großes Jahresevent",
    description: "Der MusiKonzeptag bringt Musikpädagog:innen zusammen. Workshops, Vorträge und jede Menge Inspiration – jetzt auch als Aufzeichnung verfügbar.",
    gradient: "from-indigo-500/10 via-violet-500/5 to-background",
    blob1: "bg-indigo-500/15",
    blob2: "bg-violet-500/10",
    accentColor: "text-indigo-600",
  },
  "weitere-kurse": {
    badge: "Weitere Kurse",
    badgeIcon: <GraduationCap className="w-4 h-4" />,
    title: "Noch mehr entdecken",
    description: "Hier findest du weitere spannende Kurse und Inhalte, die nicht in die anderen Kategorien passen – aber genauso wertvoll sind.",
    gradient: "from-primary/5 via-accent/5 to-background",
    blob1: "bg-primary/10",
    blob2: "bg-accent/10",
    accentColor: "text-primary",
  },
};

const Kurse = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("kategorie");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(categoryParam);
  const [searchQuery, setSearchQuery] = useState("");

  // Sync state with URL param
  useEffect(() => {
    setSelectedCategory(categoryParam);
  }, [categoryParam]);

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ["public-course-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("course_categories")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data;
    },
  });

  // Fetch published, non-hidden courses with their category assignments
  const { data: courses = [], isLoading } = useQuery({
    queryKey: ["public-courses"],
    queryFn: async () => {
      // Fetch courses
      const { data: coursesData, error: coursesError } = await supabase
        .from("courses")
        .select("id, title, slug, description, thumbnail_url, category, landing_page_price, landing_page_badge")
        .eq("is_published", true)
        .eq("is_hidden", false)
        .order("sort_order", { ascending: true });

      if (coursesError) throw coursesError;

      // Fetch all category assignments
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from("course_category_assignments")
        .select("course_id, category_id, course_categories(title)");

      if (assignmentsError) throw assignmentsError;

      // Build a map of course_id -> category titles
      const categoryMap: Record<string, string[]> = {};
      assignmentsData?.forEach((assignment: any) => {
        const courseId = assignment.course_id;
        const categoryTitle = assignment.course_categories?.title;
        if (categoryTitle) {
          if (!categoryMap[courseId]) {
            categoryMap[courseId] = [];
          }
          categoryMap[courseId].push(categoryTitle);
        }
      });

      // Merge categories into courses
      return (coursesData || []).map(course => ({
        ...course,
        categories: categoryMap[course.id] || (course.category ? [course.category] : []),
      }));
    },
  });

  // Filter courses by category and search - use categories array for multi-category support
  const filteredCourses = useMemo(() => {
    let result = courses;
    
    // Filter by category - check categories array
    if (selectedCategory) {
      const category = categories.find(c => c.slug === selectedCategory);
      if (category) {
        result = result.filter(course => {
          const courseCats = (course as any).categories || (course.category ? [course.category] : []);
          return courseCats.includes(category.title);
        });
      }
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(course => 
        course.title?.toLowerCase().includes(query) ||
        course.description?.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [courses, categories, selectedCategory, searchQuery]);

  const handleCategoryClick = (slug: string | null) => {
    setSelectedCategory(slug);
    if (slug) {
      setSearchParams({ kategorie: slug });
    } else {
      setSearchParams({});
    }
  };

  const selectedCategoryTitle = selectedCategory 
    ? categories.find(c => c.slug === selectedCategory)?.title 
    : null;

  // Get hero config for current category
  const heroConfig = selectedCategory && categoryHeroConfig[selectedCategory] 
    ? categoryHeroConfig[selectedCategory] 
    : categoryHeroConfig.default;

  // Set document title
  useEffect(() => {
    const title = selectedCategoryTitle 
      ? `${selectedCategoryTitle} | MusiKonzept Online-Akademie`
      : "Online-Kurse & Fortbildungen | MusiKonzept";
    document.title = title;
  }, [selectedCategoryTitle]);

  return (
    <div className="min-h-screen bg-background">
        <SEO title={`Kursbibliothek – ${gross(KURSE_TEXT)} | MusiKonzept`} description={`Entdecke ${KURSE_TEXT} und Kreativpakete für pädagogische Fachkräfte – Lieder, Bewegung, Instrumente und mehr.`} path="/kurse" />
        <Header />

        {/* Hero Section - Dynamic based on category */}
        <section className="relative pt-24 pb-10 overflow-hidden transition-all duration-500">
          {/* Background */}
          <div className={`absolute inset-0 bg-gradient-to-b ${heroConfig.gradient} transition-all duration-500`} />
          <div className={`absolute top-0 left-1/4 w-96 h-96 ${heroConfig.blob1} rounded-full blur-3xl transition-all duration-500`} />
          <div className={`absolute bottom-0 right-1/4 w-96 h-96 ${heroConfig.blob2} rounded-full blur-3xl transition-all duration-500`} />

          <div className="container relative z-10 mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 backdrop-blur-sm ${heroConfig.accentColor} mb-4 border border-current/20 transition-all duration-300`}>
                {heroConfig.badgeIcon}
                <span className="text-sm font-medium">{heroConfig.badge}</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 transition-all duration-300">
                {heroConfig.title}
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground mb-6 max-w-2xl mx-auto transition-all duration-300">
                {heroConfig.description}
              </p>

              {/* Search Field */}
              <div className="max-w-md mx-auto mb-6">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Kurse durchsuchen..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 pr-4 h-12 text-base rounded-full bg-white/90 backdrop-blur-sm border-border shadow-sm focus:border-primary"
                  />
                </div>
              </div>

              {/* Stats - only show on "All Courses" */}
              {!selectedCategory && (
                <div className="flex flex-wrap justify-center gap-8 mt-10">
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${heroConfig.accentColor}`}>{courses.length}+</div>
                    <div className="text-sm text-muted-foreground">Online-Kurse</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${heroConfig.accentColor}`}>{FACHKRAEFTE_KURZ}</div>
                    <div className="text-sm text-muted-foreground">Fachkräfte</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${heroConfig.accentColor}`}>{WEBSEITE_FAKTEN.gegruendet}</div>
                    <div className="text-sm text-muted-foreground">gegründet</div>
                  </div>
                </div>
              )}

              {/* Course count for categories */}
              {selectedCategory && (
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/60 backdrop-blur-sm text-muted-foreground mt-4`}>
                  <span className="text-sm">{filteredCourses.length} {filteredCourses.length === 1 ? "Kurs" : "Kurse"} verfügbar</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="sticky top-16 z-30 bg-background/95 backdrop-blur-sm border-b border-border/50 py-4">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap gap-2 justify-center">
              <button
                onClick={() => handleCategoryClick(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  !selectedCategory
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "bg-muted text-foreground/70 hover:bg-muted/80 hover:text-foreground"
                }`}
              >
                Alle Kurse
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === category.slug
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "bg-muted text-foreground/70 hover:bg-muted/80 hover:text-foreground"
                  }`}
                >
                  {category.title}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Course Grid */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            {isLoading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredCourses.length === 0 ? (
              <div className="text-center py-20">
                <GraduationCap className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">
                  Keine Kurse in dieser Kategorie gefunden.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredCourses.map((course) => (
                  <PublicCourseCard key={course.id} course={course} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-primary/10 via-accent/5 to-primary/10">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Noch Fragen zu unseren Kursen?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Wir beraten dich gerne persönlich und finden gemeinsam das passende Angebot für dich.
            </p>
            <a 
              href="/kontakt"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              Kontakt aufnehmen
            </a>
          </div>
        </section>

        <Footer />
      </div>
  );
};

export default Kurse;
